import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import GenericPromises from '../../../../api/GenericPromises';
import useSnackBar from '../../../../components/Commons/SnackBar/useSnackBar';
import { FormsErrors } from '../../../../hooks/Forms/FormsErrors';
import { Resource } from '../../../../interfaces/Security/resources';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Autocomplete, Box, Switch, TextField, Typography } from '@mui/material';
import { ButtonStyled, PrimaryButton } from '../../../../theme/buttons';
import { Permission } from '../../../../interfaces/Security/permissions';
import { Group } from '../../../../interfaces/Security/groups';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { Spinner } from '../../../../components/Commons/Spinner/Spinner';
import { useBase64 } from '../../../../hooks/useBase64';
import { Menuitem } from '../../../../interfaces/Security/menu';
import { GenericDialog } from '../../../../components/Dialogs/Dialog';

interface UpdatePermissionProps {
    permissionPayload: any,
    setOpenDialogUpdate: Dispatch<SetStateAction<boolean>>,
    resourceScreen: Menuitem | undefined,
    handleDeleteRow: (deleteRow: number) => void,
    handleUpdateRow: (updateRow: Permission) => void,
}

export const UpdatePermission = ({
    permissionPayload,
    setOpenDialogUpdate,
    resourceScreen,
    handleDeleteRow,
    handleUpdateRow,
}: UpdatePermissionProps) => {
    const [t] = useTranslation("global");
    const { GenericPutResource, GenericGetResource, GenericGetResourceGeneric, GenericDeleteResource } = GenericPromises();
    const { showSnackBar, SnackbarComponent } = useSnackBar();
    const { GetError } = FormsErrors();
    const [comboResources, setComboResources] = useState<Resource[]>([])
    const [comboGroup, setcomboGroup] = useState<Group[]>([])
    const [loadingPost, setLoadingPost] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [defaultGroup, setDefaultGroup] = useState<Group>();
    const [defaultResource, setDefaultResource] = useState<Resource>();
    const { utf8ToBase64 } = useBase64();
    const isEdit = !resourceScreen?.update;

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<Permission>({
        defaultValues: {
            group_id: undefined,
            resource_id: undefined,
            create: false,
            read: false,
            update: false,
            delete: false,
        }
    });
    const onSubmit: SubmitHandler<Permission> = (data) => onPut(data);

    const onPut = (data: Permission) => {
        let myData = {
            group_id: data.group_id,
            resource_id: data.resource_id,
            create: data.create,
            read: data.read,
            update: data.update,
            delete: data.delete,
        }
        setLoadingPost(true);
        GenericPutResource(`/permissions/${permissionPayload.permissions_id}`, myData)
            .then((response) => {
                handleUpdateRow(response.data);
                showSnackBar(t("generic.snackbar.update"), "success");
                setLoadingPost(false);
            })
            .catch((error) => {
                setLoadingPost(false);
                showSnackBar(error.message, 'error');
            });
    }

    const onDelete = () => {
        setOpenDeleteDialog(false);
        GenericDeleteResource(`/permissions/${permissionPayload?.permissions_id}`)
            .then(() => {
                setOpenDialogUpdate(false);
                handleDeleteRow(permissionPayload?.permissions_id);
            })
            .catch((error) => {
                showSnackBar(error.message, "error");
            })
    }

    const onCancel = () => {
        // validacion si quiere cancelar sus cambios 
        setOpenDialogUpdate(false);
    }

    useEffect(() => {
        GenericGetResourceGeneric("/companies", "/gcompanies").then((response) => {
            document.title = `${response.data.data[0].comercial_name} - ${t("permissions.title")}`;
        })
        let myPromises = [
            GenericGetResource("/resources"),
            GenericGetResource("/groups"),
            GenericGetResource(`/permissions?filter=${utf8ToBase64(`group_id=${permissionPayload.group_id}`)}`),
        ];
        Promise.all(myPromises)
            .then((responses) => {
                reset({
                    group_id: permissionPayload.group_id,
                    resource_id: permissionPayload.resource_id,
                    create: permissionPayload.create,
                    read: permissionPayload.read,
                    update: permissionPayload.update,
                    delete: permissionPayload.delete,
                });
                let myComboResource = responses[0].data.data;
                let myComboResourceGroup = responses[2].data.data;
                let myComboFiltered = myComboResource.filter((resource: Resource) => {
                    if (myComboResourceGroup.find((element: Permission) => element.resource_id === resource.resource_id)) {
                        return;
                    }
                    else {
                        return resource;
                    }
                })
                let myDefaultResource = myComboResource.find((element: Resource) => element.resource_id === permissionPayload.resource_id)
                setDefaultResource(myDefaultResource);
                myComboFiltered.push(myDefaultResource);
                setComboResources(myComboFiltered);
                setcomboGroup(responses[1].data.data);
                setDefaultGroup(responses[1].data.data.find((element: Group) => element.group_id === permissionPayload.group_id))
                setDataLoaded(true);
            }).catch((error) => {
                showSnackBar(error.message, "error");
                setDataLoaded(true);
            })
    }, [])

    return (
        <>
            {!dataLoaded && <Spinner isBox={false} />}
            {dataLoaded &&
                <Box className='d-flex justify-content-center' sx={{ minWidth: 1200 }}>
                    <div className='w-75'>
                        <form onSubmit={handleSubmit(onSubmit)} className='mt-4'>
                            <div className="custom-card-row d-flex justify-content-between">
                                <div className="w-100 d-flex flex-row">
                                    <div className="w-50 d-flex flex-column">
                                        <div className="w-100 d-flex flex-row">
                                            <Controller
                                                name="group_id"
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <Autocomplete
                                                        ref={field.ref}
                                                        size="small"
                                                        sx={{ width: "100%" }}
                                                        options={comboGroup}
                                                        defaultValue={defaultGroup}
                                                        getOptionLabel={(option) => `${option.group_name}`}
                                                        disabled={true}
                                                        renderOption={(props, option: Group) => (
                                                            <div key={option.group_id}>
                                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                    {`${option.group_name}`}
                                                                </Box>
                                                            </div>
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option.group_id === value.group_id}
                                                        onChange={(_, values) => {
                                                            field.onChange(values?.group_id || null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`${t("groups.title-view")}`}
                                                                variant="filled"
                                                                value={field.value}
                                                                error={!!errors.group_id}
                                                                helperText={GetError(errors.group_id?.type)}

                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="w-100 d-flex flex-row mt-2">
                                            <Controller
                                                name="resource_id"
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <Autocomplete
                                                        ref={field.ref}
                                                        size="small"
                                                        sx={{ width: "100%" }}
                                                        options={comboResources}
                                                        defaultValue={defaultResource}
                                                        getOptionLabel={(option) => `${option.resource_name} - ${option.resource_route}`}
                                                        renderOption={(props, option: Resource) => (
                                                            <div key={option.resource_id}>
                                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                    {`${option.resource_name} - ${option.resource_route}`}
                                                                </Box>
                                                            </div>
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option.resource_id === value.resource_id}
                                                        onChange={(_, values) => {
                                                            field.onChange(values?.resource_id || null);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`${t("resources.title-view")}`}
                                                                variant="filled"
                                                                value={field.value}
                                                                error={!!errors.resource_id}
                                                                helperText={GetError(errors.resource_id?.type)}
                                                            />
                                                        )}
                                                        disabled={isEdit}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="w-50 d-flex flex-column">
                                        <div className="w-100 d-flex justify-content-center align-items-center">
                                            <Typography variant="h6" gutterBottom>{t("permissions.info.permissions")} :</Typography>
                                        </div>
                                        <div className="w-100 d-flex flex-row-reverse">
                                            <Controller
                                                name="read"
                                                control={control}
                                                render={({ field }) =>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '55%', flexDirection: 'row-reverse' }}>
                                                        <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={permissionPayload.read} disabled={isEdit} />
                                                        <Typography sx={{ mr: 1 }}>{t("permissions.fields.read")}</Typography>
                                                        <AutoStoriesIcon sx={{ mr: 1 }} />
                                                    </Box>
                                                }
                                            />
                                            <Controller
                                                name="create"
                                                control={control}
                                                render={({ field }) =>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '55%', flexDirection: 'row-reverse' }}>
                                                        <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={permissionPayload.create} disabled={isEdit} />
                                                        <Typography sx={{ mr: 1 }}>{t("permissions.fields.create")}</Typography>
                                                        <AddCircleOutlineIcon sx={{ mr: 1 }} />
                                                    </Box>
                                                }
                                            />
                                        </div>
                                        <div className="w-100 d-flex flex-row-reverse mt-1">
                                            <Controller
                                                name="delete"
                                                control={control}
                                                render={({ field }) =>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '55%', flexDirection: 'row-reverse' }}>
                                                        <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={permissionPayload.delete} disabled={isEdit} />
                                                        <Typography sx={{ mr: 1 }}>{t("permissions.fields.delete")}</Typography>
                                                        <DeleteIcon sx={{ mr: 1 }} />
                                                    </Box>
                                                }
                                            />
                                            <Controller
                                                name="update"
                                                control={control}
                                                render={({ field }) =>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '55%', flexDirection: 'row-reverse' }}>
                                                        <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={permissionPayload.update} disabled={isEdit} />
                                                        <Typography sx={{ mr: 1 }}>{t("permissions.fields.update")}</Typography>
                                                        <EditIcon sx={{ mr: 1 }} />
                                                    </Box>
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='d-flex flex-row-reverse justify-content-between mt-1'>
                                <Box display={"flex"} alignItems={"center"}>
                                    <ButtonStyled variant='outlined' className='m-1' onClick={onCancel}>{t("generic.buttons.goback")}</ButtonStyled>
                                    <PrimaryButton type='submit' variant='outlined' className='m-1' disabled={loadingPost}>{t("generic.buttons.accept")}</PrimaryButton>
                                </Box>
                                <Box display={"flex"} >
                                    {resourceScreen?.delete ?
                                        <PrimaryButton onClick={() => setOpenDeleteDialog(true)}>{t("generic.buttons.delete")}</PrimaryButton> : <></>
                                    }
                                </Box>
                            </div>

                        </form>

                    </div >
                    <GenericDialog
                        title={t("generic.dialog.delete")}
                        content={t("permissions.dialog.delete")}
                        open={openDeleteDialog}
                        setOpen={setOpenDeleteDialog}
                        onAcept={onDelete}
                    />
                    <SnackbarComponent />
                </Box>
            }
        </>
    )
}
